// Teacher Timetable Grid Component - SAT V4 Zone
import { DsCard, DsTypography } from '@ice-web-app/designSystem';
import { Box, Chip, Stack } from '@mui/material';
import type { FC } from 'react';
import type { Availability, DayOfWeek, Period } from '../../types';
import { TeacherSlotCard } from './TeacherSlotCard';
import type { SlotStatus, TeacherDuty, TeacherScheduledLesson } from './TeacherSlotCard';

interface TeacherTimetableGridProps {
  periods: Period[];
  availability: Availability;
  scheduledLessons?: TeacherScheduledLesson[];
  duties?: TeacherDuty[];
  selectedWeek?: 'A' | 'B';
  showWeekends?: boolean;
  compact?: boolean;
  onSlotClick?: (day: DayOfWeek, periodId: string) => void;
}

const WEEKDAYS: DayOfWeek[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
const ALL_DAYS: DayOfWeek[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const WEEKEND_DAYS: DayOfWeek[] = ['saturday', 'sunday'];

export const TeacherTimetableGrid: FC<TeacherTimetableGridProps> = ({
  periods = [],
  availability = {},
  scheduledLessons = [],
  duties = [],
  selectedWeek,
  showWeekends = true,
  compact = false,
  onSlotClick,
}) => {
  const days = showWeekends ? ALL_DAYS : WEEKDAYS;

  // Get ALL unique periods sorted by order (show all period types)
  const sortedPeriods = [...periods]
    .filter((p, i, arr) => arr.findIndex((x) => x.order === p.order) === i)
    .sort((a, b) => a.order - b.order);

  const formatTime = (time: string) => time.substring(0, 5);

  const getScheduledLesson = (day: DayOfWeek, periodId: string): TeacherScheduledLesson | undefined => {
    return scheduledLessons.find((lesson) => {
      const matchesDay = lesson.dayOfWeek === day;
      const matchesPeriod = lesson.periodId === periodId;
      const matchesWeek = !selectedWeek || !lesson.week || lesson.week === selectedWeek;
      return matchesDay && matchesPeriod && matchesWeek;
    });
  };

  const getDuty = (day: DayOfWeek, periodId: string): TeacherDuty | undefined => {
    return duties.find((duty) => {
      const matchesDay = duty.dayOfWeek === day;
      const matchesPeriod = duty.periodId === periodId;
      const matchesWeek = !selectedWeek || !duty.week || duty.week === selectedWeek;
      return matchesDay && matchesPeriod && matchesWeek;
    });
  };

  const getSlotStatus = (day: DayOfWeek, periodId: string): SlotStatus => {
    // Saturday and Sunday are always unavailable
    if (WEEKEND_DAYS.includes(day)) {
      return 'unavailable';
    }

    // Check if there's a scheduled lesson for this slot
    const lesson = getScheduledLesson(day, periodId);
    if (lesson) {
      return 'teaching';
    }

    // Check if there's a duty for this slot
    const duty = getDuty(day, periodId);
    if (duty) {
      return duty.dutyType === 'other' ? 'other' : 'duty';
    }

    // Otherwise return availability status
    const availabilityStatus = availability[day]?.[periodId];
    if (availabilityStatus === 'unavailable') return 'unavailable';
    if (availabilityStatus === 'preferred') return 'preferred';
    return 'available';
  };

  const cellWidth = compact ? 100 : 140;
  const headerHeight = 48;
  const timeColumnWidth = compact ? 100 : 120;

  return (
    <DsCard sx={{ overflow: 'auto' }}>
      <Box sx={{ minWidth: days.length * cellWidth + timeColumnWidth }}>
        {/* Header Row - Days */}
        <Stack direction="row">
          {/* Empty corner cell */}
          <Box
            sx={{
              width: timeColumnWidth,
              minWidth: timeColumnWidth,
              height: headerHeight,
              bgcolor: 'background.default',
              borderBottom: 1,
              borderRight: 1,
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <DsTypography variant="caption" fontWeight={600} color="text.secondary">
              Session / Period
            </DsTypography>
          </Box>

          {/* Day headers */}
          {days.map((day) => (
            <Box
              key={day}
              sx={{
                width: cellWidth,
                minWidth: cellWidth,
                height: headerHeight,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'primary.main',
                borderBottom: 1,
                borderRight: 1,
                borderColor: 'divider',
              }}
            >
              <DsTypography variant="subtitle2" fontWeight={600} sx={{ textTransform: 'capitalize', color: 'white' }}>
                {day.slice(0, 3)}
              </DsTypography>
            </Box>
          ))}
        </Stack>

        {/* Period Rows */}
        {sortedPeriods.map((period) => (
          <Stack direction="row" key={period.id}>
            {/* Period Label */}
            <Box
              sx={{
                width: timeColumnWidth,
                minWidth: timeColumnWidth,
                p: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'background.default',
                borderBottom: 1,
                borderRight: 1,
                borderColor: 'divider',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Chip
                  label={period.order}
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, minWidth: 24, fontSize: 11 }}
                />
                <DsTypography variant="caption" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
                  {period.name}
                </DsTypography>
              </Stack>
              <DsTypography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                {formatTime(period.startTime)} - {formatTime(period.endTime)}
              </DsTypography>
            </Box>

            {/* Day Cells */}
            {days.map((day) => {
              const status = getSlotStatus(day, period.id);
              const lesson = status === 'teaching' ? getScheduledLesson(day, period.id) : undefined;
              const duty = status === 'duty' || status === 'other' ? getDuty(day, period.id) : undefined;

              return (
                <Box
                  key={`${day}-${period.id}`}
                  sx={{
                    width: cellWidth,
                    minWidth: cellWidth,
                    p: 0.5,
                    borderBottom: 1,
                    borderRight: 1,
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                  }}
                >
                  <TeacherSlotCard
                    status={status}
                    lesson={lesson}
                    duty={duty}
                    compact={compact}
                    onClick={onSlotClick ? () => onSlotClick(day, period.id) : undefined}
                  />
                </Box>
              );
            })}
          </Stack>
        ))}
      </Box>

      {/* Legend */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'background.default' }}>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" flexWrap="wrap" useFlexGap>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Box
              sx={{
                width: 16,
                height: 16,
                borderRadius: 1,
                bgcolor: '#4CAF5020',
                borderLeft: 3,
                borderColor: '#4CAF50',
              }}
            />
            <DsTypography variant="caption">Teaching</DsTypography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Box
              sx={{
                width: 16,
                height: 16,
                borderRadius: 1,
                bgcolor: '#FFF3E0',
                borderLeft: 3,
                borderColor: '#FF9800',
              }}
            />
            <DsTypography variant="caption">On Duty</DsTypography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Box
              sx={{
                width: 16,
                height: 16,
                borderRadius: 1,
                bgcolor: '#E0F2F1',
                borderLeft: 3,
                borderColor: '#00796B',
              }}
            />
            <DsTypography variant="caption">Available</DsTypography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Box
              sx={{
                width: 16,
                height: 16,
                borderRadius: 1,
                bgcolor: '#FFEBEE',
                borderLeft: 3,
                borderColor: '#F44336',
              }}
            />
            <DsTypography variant="caption">Unavailable</DsTypography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Box
              sx={{
                width: 16,
                height: 16,
                borderRadius: 1,
                bgcolor: '#F3E5F5',
                borderLeft: 3,
                borderColor: '#9C27B0',
              }}
            />
            <DsTypography variant="caption">Other Duties</DsTypography>
          </Stack>
        </Stack>
      </Box>
    </DsCard>
  );
};

export default TeacherTimetableGrid;
